import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { BlogPost, SuccessStory } from '../../types/content';
import { ContentService } from '../../services/contentService';

interface ContentState {
  content: (BlogPost | SuccessStory)[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  searchQuery: string;
}

// Load persisted state from localStorage
const loadPersistedState = () => {
  try {
    const searchQuery = localStorage.getItem('hcltech_content_search');
    return {
      searchQuery: searchQuery || '',
    };
  } catch (error) {
    return {
      searchQuery: '',
    };
  }
};

const persistedState = loadPersistedState();

const initialState: ContentState = {
  content: [],
  loading: false,
  error: null,
  lastFetched: null,
  searchQuery: persistedState.searchQuery,
};

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

// Async thunk to fetch all content
export const fetchAllContent = createAsyncThunk(
  'content/fetchAll',
  async (_, { getState }) => {
    const state = getState() as { content: ContentState };
    const now = Date.now();
    
    // Check if cache is still valid
    if (
      state.content.lastFetched &&
      now - state.content.lastFetched < CACHE_DURATION &&
      state.content.content.length > 0
    ) {
      // Return cached data
      return state.content.content;
    }
    
    // Fetch fresh data
    const content = await ContentService.getAllContent();
    return content;
  }
);

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    clearSearchQuery: (state) => {
      state.searchQuery = '';
    },
    clearContentCache: (state) => {
      state.content = [];
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllContent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllContent.fulfilled, (state, action) => {
        state.loading = false;
        state.content = action.payload;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchAllContent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load content';
      });
  },
});

export const { setSearchQuery, clearSearchQuery, clearContentCache } = contentSlice.actions;
export default contentSlice.reducer;
